<template>
  <v-dialog :value="value" max-width="560" persistent @keydown.esc="close">
    <v-card v-if="token">
      <v-card-title class="text-h6">{{ $t('rfid.withdrawal.title', { uid: token.uid }) }}</v-card-title>
      <v-card-text>
        <p class="text-body-2">{{ $t('rfid.withdrawal.explained') }}</p>
        <v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>
        <v-radio-group v-model="outcome" class="mt-0" hide-details :disabled="working">
          <v-radio v-for="option in outcomes" :key="option" :value="option">
            <template #label>
              <div>
                <div>{{ $t(`rfid.withdrawal.outcome.${option}`) }}</div>
                <div class="text-caption text--secondary">{{ $t(`rfid.withdrawal.outcome-hint.${option}`) }}</div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <v-select
            v-if="outcome === 'returned'" v-model="locationId" :items="locations" item-text="name" item-value="id"
            dense outlined hide-details :label="$t('rfid.withdrawal.location')" class="mt-4" :disabled="working"
        />
        <v-text-field v-model="comment" dense outlined hide-details :label="$t('rfid.withdrawal.comment')" class="mt-4" :disabled="working" />
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn small text :disabled="working" @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn small color="error" :loading="working" :disabled="!ready" @click="submit">{{ $t('rfid.withdrawal.submit') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BadgeInventoryRepository from '@repository/BadgeInventoryRepository'
import TokenRepository from '@repository/TokenRepository'
import BadgeInventoryMixin from '@pages/BadgeInventory/BadgeInventoryMixin'

/** Ce que devient un badge retiré à son titulaire, tel que le serveur l'énumère (`BadgeWithdrawal`). */
const OUTCOMES = ['recovered', 'returned', 'lost', 'stolen', 'destroyed']

/**
 * Supprimer un badge du stock depuis le portail : on dit ce qu'il devient. Récupéré, il est à redonner dans
 * l'organisation ; rendu à ZEborne, il revient au dépôt (ZEborne seul) ; perdu, volé ou détruit, il sort du service —
 * jamais pour toujours : s'il est remis ou réactivé un jour, il revit.
 */
export default {
  name: 'BadgeWithdrawalDialog',

  mixins: [BadgeInventoryMixin],

  props: {
    value: { type: Boolean, default: false },
    /** Le token du badge (son `id`, son `uid`). */
    token: { type: Object, default: null },
  },

  data() {
    return { outcome: null, locationId: null, comment: '', locations: [], working: false, error: null }
  },

  computed: {
    /** Rendre au dépôt : ZEborne seul, qui gère le stock. */
    canReturnToDepot() {
      return !!this.currentUser?.isZE && (this.currentUser.permissions || []).includes('BADGE_STOCK_MANAGE')
    },

    outcomes() {
      return OUTCOMES.filter(o => o !== 'returned' || this.canReturnToDepot)
    },

    ready() {
      return !!this.outcome && (this.outcome !== 'returned' || !!this.locationId)
    },
  },

  watch: {
    value(open) {
      if (!open) return
      Object.assign(this, { outcome: null, locationId: null, comment: '', working: false, error: null })
      if (this.canReturnToDepot && !this.locations.length) {
        BadgeInventoryRepository.catalogue().then(c => { this.locations = c.locations || [] }).catch(() => {})
      }
    },
  },

  methods: {
    close() {
      this.$emit('input', false)
    },

    async submit() {
      this.working = true
      this.error = null
      try {
        await TokenRepository.delete(this.token.id, {
          outcome: this.outcome,
          locationId: this.outcome === 'returned' ? this.locationId : null,
          comment: this.comment.trim() || null,
        })
        this.$emit('deleted')
        this.close()
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },
  },
}
</script>
